import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { DataOverviewStats, loadDataOverviewStats } from '../lib/storage/dataOverview';
import { getSavedCurves } from '../lib/storage/curves';
import { fixedBondStore, floatingBondStore } from '../lib/storage/bonds';
import { getQuoteBook, saveQuoteBook } from '../lib/storage/quoteBook';
import { indexStore } from '../lib/storage/indices';
import { replaceIrSwaps } from '../lib/storage/swaps';
import { replaceInflationSwaps } from '../lib/storage/inflationSwaps';
import { replaceSwaptions } from '../lib/storage/swaptions';
import { replaceSwaptionModels } from '../lib/storage/swaptionModels';
import { replaceCds } from '../lib/storage/cds';
import { replaceCreditCurves } from '../lib/storage/creditCurves';
import { replaceEquityOptions } from '../lib/storage/equityOptions';

interface ExportData {
  indices?: any[];
  curves?: any[];
  curveSets?: any[];
  quotes?: any[];
  quoteBook?: any[];
  volSurfaces?: any[];
  creditCurves?: any[];
  fixedBonds?: any[];
  floatingBonds?: any[];
  swaps?: any[];
  inflationSwaps?: any[];
  swaptions?: any[];
  swaptionModels?: any[];
  cds?: any[];
  equityOptions?: any[];
}

export default function Home() {
  const [stats, setStats] = useState<DataOverviewStats | null>(null);
  const [importingExample, setImportingExample] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    void loadDataOverviewStats().then(setStats);
  }, []);

  function getQuotes(): any[] {
    try {
      const raw = localStorage.getItem('quantra_quotes');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  function getCurveSets(): any[] {
    try {
      const raw = localStorage.getItem('quantra_curve_sets');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  async function importBackupData(data: ExportData, includeMarketData: boolean) {
    if (data.indices && Array.isArray(data.indices)) {
      for (const index of data.indices) {
        if (!index?.id) continue;
        await indexStore.save(index as any);
      }
    }

    if (data.curves && Array.isArray(data.curves)) {
      const existing = getSavedCurves();
      for (const curve of data.curves) {
        if (!curve?.id || !curve?.points) continue;
        const idx = existing.findIndex((c: any) => c.id === curve.id);
        if (idx === -1) existing.push(curve);
        else existing[idx] = curve;
      }
      localStorage.setItem('quantra_curves', JSON.stringify(existing));
    }

    if (includeMarketData && data.quotes && Array.isArray(data.quotes)) {
      const existing = getQuotes();
      for (const quote of data.quotes) {
        if (!quote?.id) continue;
        const idx = existing.findIndex((q: any) => q.id === quote.id);
        if (idx === -1) existing.push(quote);
        else existing[idx] = quote;
      }
      localStorage.setItem('quantra_quotes', JSON.stringify(existing));
    }

    if (data.curveSets && Array.isArray(data.curveSets)) {
      const existing = getCurveSets();
      for (const cs of data.curveSets) {
        if (!cs?.id) continue;
        const idx = existing.findIndex((x: any) => x.id === cs.id);
        if (idx === -1) existing.push(cs);
        else existing[idx] = cs;
      }
      localStorage.setItem('quantra_curve_sets', JSON.stringify(existing));
    }

    if (data.fixedBonds && Array.isArray(data.fixedBonds)) {
      for (const bond of data.fixedBonds) {
        if (bond?.id) await fixedBondStore.save(bond);
      }
    }

    if (data.floatingBonds && Array.isArray(data.floatingBonds)) {
      for (const bond of data.floatingBonds) {
        if (bond?.id) await floatingBondStore.save(bond);
      }
    }

    if (data.swaps && Array.isArray(data.swaps)) {
      await replaceIrSwaps(data.swaps);
    }

    if (data.inflationSwaps && Array.isArray(data.inflationSwaps)) {
      await replaceInflationSwaps(data.inflationSwaps);
    }

    if (data.swaptions && Array.isArray(data.swaptions)) {
      await replaceSwaptions(data.swaptions);
    }

    if (data.swaptionModels && Array.isArray(data.swaptionModels)) {
      replaceSwaptionModels(data.swaptionModels);
    }

    if (data.cds && Array.isArray(data.cds)) {
      await replaceCds(data.cds);
    }
    if (data.equityOptions && Array.isArray(data.equityOptions)) {
      await replaceEquityOptions(data.equityOptions);
    }

    if (includeMarketData && data.quoteBook && Array.isArray(data.quoteBook)) {
      const existing = getQuoteBook();
      for (const entry of data.quoteBook) {
        if (!entry?.id || !Array.isArray(entry.series)) continue;
        const idx = existing.findIndex((e) => e.id === entry.id);
        if (idx === -1) existing.push(entry);
        else existing[idx] = entry;
      }
      saveQuoteBook(existing);
    }

    if (includeMarketData && data.volSurfaces && Array.isArray(data.volSurfaces)) {
      localStorage.setItem('quantra_vol_surfaces', JSON.stringify(data.volSurfaces));
    }

    if (data.creditCurves && Array.isArray(data.creditCurves)) {
      await replaceCreditCurves(data.creditCurves);
    }
  }

  async function handleLoadFullExample() {
    if (!confirm('Load full example (portfolio + market data)? This will overwrite existing data.')) {
      return;
    }
    setImportingExample(true);
    setMessage(null);
    try {
      const backupResponse = await fetch('/example-backup.json');
      if (!backupResponse.ok) throw new Error('Failed to load example backup');
      const backupData = (await backupResponse.json()) as ExportData;
      await importBackupData(backupData, false);

      const marketResponse = await fetch('/example-market-data.json');
      if (!marketResponse.ok) throw new Error('Failed to load example market data');
      const marketData = (await marketResponse.json()) as ExportData;
      await importBackupData(marketData, true);

      setStats(await loadDataOverviewStats());
      setMessage({ type: 'success', text: 'Loaded full example (portfolio + market data)' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to load full example' });
    } finally {
      setImportingExample(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-12">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-[#0a0a0a]">Home</h1>
          <p className="text-[#737373] mt-1">Workspace snapshot and quick entry points.</p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        <section className="bg-white border border-[#e5e5e5] rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#0a0a0a]">Loaded Entities</h2>
            <Link to="/settings" className="text-xs text-[#8a6a2f] hover:underline">View in Settings</Link>
          </div>
          {!stats ? (
            <p className="text-sm text-[#737373]">Loading...</p>
          ) : (
            <div className="overflow-hidden border border-[#e5e5e5] rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-[#fafafa] border-b border-[#e5e5e5]">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-[#525252] uppercase tracking-wide">Entity</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-[#525252] uppercase tracking-wide">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Curves', stats.curves],
                    ['Curve Sets', stats.curveSets],
                    ['Vol Surfaces', stats.volSurfaces],
                    ['Credit Curves', stats.creditCurves],
                    ['Products (all)', stats.fixedBonds + stats.floatingBonds + stats.swaps + stats.inflationSwaps + stats.swaptions + stats.cds + stats.equityOptions],
                    ['Swaption Models', stats.swaptionModels],
                    ['Market Data (indices + quotes + quote book)', stats.indices + stats.quotes + stats.quoteBookEntries],
                  ].map(([label, value]) => (
                    <tr key={String(label)} className="border-t border-[#f0f0f0]">
                      <td className="px-3 py-1.5 text-[#525252]">{label}</td>
                      <td className="px-3 py-1.5 text-right font-medium text-[#0a0a0a]">{value as number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {stats && stats.total === 0 && (
            <div className="mt-4 border border-[#e5e5e5] rounded-lg p-4 bg-[#fafafa]">
              <p className="text-sm text-[#525252] mb-3">No entities loaded yet. Load the full example to get started.</p>
              <button
                onClick={handleLoadFullExample}
                disabled={importingExample}
                className="px-4 py-2 text-sm font-medium text-white bg-[#8a6a2f] rounded-lg hover:bg-[#7a5c28] disabled:opacity-60 transition-colors"
              >
                {importingExample ? 'Loading Example...' : 'Load Full Example'}
              </button>
            </div>
          )}
        </section>

        <section className="bg-white border border-[#e5e5e5] rounded-xl p-5">
          <h2 className="text-sm font-semibold text-[#0a0a0a] mb-3">Quick Start</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {[
              ['/yield-curves', 'Yield Curves'],
              ['/inflation-curves', 'Inflation Curves'],
              ['/curve-sets', 'Curve Sets'],
              ['/vol-workbench', 'Volatilities'],
              ['/products/ir-swap', 'Swaps'],
              ['/products/inflation-swaps', 'Inflation Swaps'],
              ['/products/equity-options', 'Equity Options'],
              ['/products/swaption', 'Swaptions'],
              ['/models/swaption', 'Swaption Models'],
              ['/dashboard', 'API Playground'],
            ].map(([to, label]) => (
              <Link
                key={to}
                to={to}
                className="px-3 py-2 text-sm border border-[#e5e5e5] rounded-lg hover:bg-[#f5f5f5] text-[#525252] hover:text-[#0a0a0a] transition-colors"
              >
                {label}
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
